import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatDate';
import { Announcement, AnnouncementType, AnnouncementPriority } from '../types';
import {
  adminListAnnouncements,
  adminCreateAnnouncement,
  adminUpdateAnnouncement,
  adminPublishAnnouncement,
  adminArchiveAnnouncement,
  adminDeleteAnnouncement,
} from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

const TYPE_KEYS: AnnouncementType[] = ['feature', 'update', 'maintenance', 'alert'];
const PRIORITY_KEYS: AnnouncementPriority[] = ['normal', 'high', 'urgent'];

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--text-3)',
  published: 'var(--success)',
  archived: 'var(--text-3)',
};

const AnnouncementManager: React.FC = () => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    type: 'update' as AnnouncementType,
    priority: 'normal' as AnnouncementPriority,
  });
  const [saving, setSaving] = useState(false);

  // confirm dialog
  const [confirmAction, setConfirmAction] = useState<{ type: string; id: string; title: string } | null>(null);

  const loadData = useCallback(() => {
    setLoading(true);
    adminListAnnouncements(page, 20)
      .then(res => {
        setAnnouncements(res.items || []);
        setTotal(res.total);
      })
      .catch(() => toast.error(t('announcements.loadFailed')))
      .finally(() => setLoading(false));
  }, [page, toast, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ title: '', content: '', type: 'update', priority: 'normal' });
    setShowModal(true);
  };

  const openEdit = (a: Announcement) => {
    setEditingId(a.id);
    setForm({ title: a.title, content: a.content, type: a.type, priority: a.priority });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error(t('announcements.titleEmpty')); return; }
    if (form.title.length > 100) { toast.error(t('announcements.titleTooLong')); return; }
    if (form.content.length > 5000) { toast.error(t('announcements.contentTooLong')); return; }
    setSaving(true);
    try {
      if (editingId) {
        await adminUpdateAnnouncement(editingId, form);
        toast.success(t('announcements.updateSuccess'));
      } else {
        await adminCreateAnnouncement(form);
        toast.success(t('announcements.createSuccess'));
      }
      setShowModal(false);
      loadData();
    } catch (e: unknown) {
      toast.error(t('announcements.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === 'publish') {
        await adminPublishAnnouncement(confirmAction.id);
        toast.success(t('announcements.published'));
      } else if (confirmAction.type === 'archive') {
        await adminArchiveAnnouncement(confirmAction.id);
        toast.success(t('announcements.archived'));
      } else if (confirmAction.type === 'delete') {
        await adminDeleteAnnouncement(confirmAction.id);
        toast.success(t('announcements.deleted'));
      }
      loadData();
    } catch (e: unknown) {
      toast.error(t('announcements.operationFailed'));
    }
    setConfirmAction(null);
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('announcements.breadcrumbHome'), to: '/projects' }, { label: t('announcements.title') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('announcements.title')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('announcements.createAnnouncement')}</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('loading', { ns: 'common' })}</div>
      ) : announcements.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('announcements.noAnnouncements')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {announcements.map(a => {
            const statusColor = STATUS_COLORS[a.status] || STATUS_COLORS.draft;
            const statusLabel = t(`announcements.statusLabels.${a.status}`, { defaultValue: a.status });
            return (
              <div key={a.id} className="card" style={{ padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '0.7rem', padding: '0.15rem 0.5rem',
                    borderRadius: '4px', fontWeight: 600,
                    color: statusColor,
                    border: `1px solid ${statusColor}`,
                  }}>
                    {statusLabel}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                    {t(`announcements.typeOptions.${a.type}`, { defaultValue: a.type })}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                    {t('announcements.priority', { priority: t(`announcements.priorityOptions.${a.priority}`, { defaultValue: a.priority }) })}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {a.publishedAt ? t('announcements.publishedAt', { date: formatDate(a.publishedAt) }) : t('announcements.createdAtLabel', { date: formatDate(a.createdAt) })}
                  </span>
                </div>

                <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.25rem' }}>{a.title}</div>
                {a.content && (
                  <div style={{
                    fontSize: '0.82rem', color: 'var(--text-3)',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
                  }}>
                    {a.content}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                  {a.status === 'draft' && (
                    <>
                      <button className="btn btn-ghost" style={{ fontSize: '0.78rem' }} onClick={() => openEdit(a)}>{t('announcements.edit')}</button>
                      <button className="btn btn-primary" style={{ fontSize: '0.78rem' }} onClick={() => setConfirmAction({ type: 'publish', id: a.id, title: a.title })}>
                        {t('announcements.publish')}
                      </button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.78rem', color: 'var(--danger)' }} onClick={() => setConfirmAction({ type: 'delete', id: a.id, title: a.title })}>
                        {t('delete', { ns: 'common' })}
                      </button>
                    </>
                  )}
                  {a.status === 'published' && (
                    <button className="btn btn-ghost" style={{ fontSize: '0.78rem' }} onClick={() => setConfirmAction({ type: 'archive', id: a.id, title: a.title })}>
                      {t('announcements.archive')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ fontSize: '0.8rem' }}>{t('users.prevPage')}</button>
          <span style={{ lineHeight: '2rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>{page} / {totalPages}</span>
          <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ fontSize: '0.8rem' }}>{t('users.nextPage')}</button>
        </div>
      )}

      {/* Edit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={editingId ? t('announcements.editAnnouncement') : t('announcements.createAnnouncementTitle')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>{t('announcements.titleLabel')}</label>
            <input
              className="input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={t('announcements.titlePlaceholder')}
              maxLength={100}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>{t('announcements.typeLabel')}</label>
              <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as AnnouncementType }))} style={{ width: '100%' }}>
                {TYPE_KEYS.map(k => <option key={k} value={k}>{t(`announcements.typeOptions.${k}`)}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>{t('announcements.priorityLabel')}</label>
              <select className="input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as AnnouncementPriority }))} style={{ width: '100%' }}>
                {PRIORITY_KEYS.map(k => <option key={k} value={k}>{t(`announcements.priorityOptions.${k}`)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.3rem', display: 'block' }}>{t('announcements.contentLabel')}</label>
            <textarea
              className="input"
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder={t('announcements.contentPlaceholder')}
              rows={6}
              maxLength={5000}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>{t('cancel', { ns: 'common' })}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title={
          confirmAction?.type === 'publish' ? t('announcements.confirmPublish') :
          confirmAction?.type === 'archive' ? t('announcements.confirmArchive') :
          t('announcements.confirmDelete')
        }
        message={
          confirmAction?.type === 'publish' ? t('announcements.publishMessage', { title: confirmAction?.title }) :
          confirmAction?.type === 'archive' ? t('announcements.archiveMessage', { title: confirmAction?.title }) :
          t('announcements.deleteMessage', { title: confirmAction?.title })
        }
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
        confirmText={confirmAction?.type === 'delete' ? t('delete', { ns: 'common' }) : t('confirm', { ns: 'common' })}
        confirmDanger={confirmAction?.type === 'delete'}
      />
    </div>
  );
};

export default AnnouncementManager;
